/**
 * Strings.
 *
 * English and Puerto Rican Spanish are both first-class from the first commit.
 * Retrofitting i18n means finding every string already inlined in a component,
 * and on this account that has meant a translation layer that only ever covered
 * the pages someone remembered.
 *
 * `es-PR`, not generic `es`: the wording is Puerto Rican, and the distinction
 * matters to the people reading it.
 */

export type Locale = 'en' | 'es-PR'

const STRINGS = {
  en: {
    'app.signIn': 'Sign in',
    'app.signOut': 'Sign out',
    'app.loading': 'Loading…',

    'auth.handle': 'Username',
    'auth.handlePlaceholder': 'yourname',
    'auth.welcome': 'Welcome aboard',
    'auth.welcomeSub': 'Enter your username. If you are new, this makes your account.',
    'auth.continue': 'Continue with a passkey',
    'auth.working': 'One moment…',
    'auth.displayName': 'Your name',
    'auth.createAccount': 'Create account',
    'auth.usePasskey': 'Sign in with a passkey',
    'auth.noPasskeySupport': 'This browser cannot use passkeys. Try Chrome, Safari or Edge.',
    'auth.setupToken': 'Setup code',
    'auth.haveSetupCode': 'Have a setup code?',
    'auth.handleTaken': 'That username is not available.',
    'auth.setupTokenWhy': 'Only needed when setting up a new install.',
    'auth.setupTokenWrong': 'That setup code is not right.',
    'auth.reassurePlatform': 'No password to make up or forget. You sign in the way you already unlock your phone — Face ID, a fingerprint, or your password manager.',
    'auth.reassureKey': 'No password to make up or forget. Use your password manager, a security key, or your phone.',
    'auth.cancelled': 'That was cancelled. Nothing has changed.',

    'account.passkeys': 'Passkeys',
    'account.addPasskey': 'Add a passkey',
    'account.removePasskey': 'Remove',
    'account.synced': 'Synced to your password manager',
    'account.deviceOnly': 'This device only',
    'account.neverUsed': 'Never used',
    'account.lastUsed': 'Last used',
    'account.recoveryWarning':
      'This account has one passkey and it is not synced. Lose that device and the account cannot be recovered — add a second passkey.',

    'notify.enable': 'Turn on notifications',
    'notify.enabled': 'Notifications are on',
    'notify.blocked': 'Notifications are blocked in your browser settings.',
    'notify.inbox': 'Notifications',
    'notify.empty': 'Nothing yet.',

    'admin.title': 'Admin',
    'admin.users': 'Users',
    'admin.audit': 'Activity',
    'admin.errors': 'Errors',
    'admin.flags': 'Flags',
    'admin.settings': 'Settings',
    'admin.broadcast': 'Broadcast',
    'admin.devices': 'Devices',

    'env.beta': 'BETA — this is not the live app',
    'env.local': 'LOCAL',

    'error.generic': 'Something went wrong.',
    'error.offline': 'You appear to be offline.',

    'update.ready': 'A newer version is ready',
    'update.now': 'Update now',
    'update.later': 'Later',

    'install.title': 'Put this on your home screen',
    'install.why': 'It gets its own icon and opens without the browser bar.',
    'install.ios': 'Tap Share, then “Add to Home Screen”.',
    'install.add': 'Add it',
    'install.no': 'No thanks',

    'nav.book': 'Book Ferry',
    'nav.trips': 'My Trips',
    'nav.account': 'Account',
    'nav.gate': 'Gate',
    'nav.ops': 'Operations',
    'nav.report': 'Position',

    'sail.title': 'Upcoming Ferries',
    'sail.back': 'All ferries',
    'sail.date': 'Date',
    'sail.route': 'Route',
    'sail.allRoutes': 'All routes',
    'sail.none': 'No sailings scheduled for this day.',
    'sail.seatsLeft': 'seats left',
    'sail.full': 'Full',
    'sail.cancelled': 'Cancelled',
    // The operator footnotes two Culebra Saturday runs as carrying hazardous
    // material. They are real boats and they are shown; nobody is sold a seat.
    'sail.cargoOnly': 'Cargo only',
    'sail.hazmat': 'Hazardous-material sailing. It does not take passengers.',
    'sail.cargoNotice': 'Cargo-only sailing. It does not take passengers.',
    'sail.unconfirmed': 'Not confirmed',
    'sail.unconfirmedNotice': 'The timetable does not yet say whether this sailing takes passengers, so it is not sold here. Check with the terminal.',
    // Shown when a footnote moved this date's departure, so the app and the
    // printed timetable in the terminal can be seen to disagree on purpose.
    'sail.printedAs': 'The printed timetable says',
    'sail.arrived': 'Arrived',
    'sail.underway': 'Underway',
    'sail.boarding': 'Boarding now',
    'sail.late': 'min late',
    'sail.book': 'Book trip (preview)',
    'sail.signInToBook': 'Sign in to book.',
    'sail.carsAndPeople': 'Cars and passengers',
    'sail.peopleOnly': 'Passengers only',
    'sail.cargo': 'Cargo',
    'sail.departs': 'Departs',
    'sail.booked': 'booked',

    'book.who': 'Who is travelling',
    'book.passenger': 'Passenger',
    'book.name': 'Name',
    'book.nameHint': 'Same as ID',
    'book.fare': 'Fare',
    'book.adult': 'Adult',
    'book.child': 'Child',
    'book.senior': '55 and over',
    'book.priority': 'Needs priority boarding — 55 or over, injured, or limited mobility',
    'book.priorityNote': 'Anything the crew should know (optional)',
    'book.remove': 'Remove',
    'book.addPassenger': 'Add another passenger',
    'book.tooMany': 'Parties over 12 are booked at the counter, so the crew can plan the deck.',
    'book.bringing': 'Taking anything with you',
    'book.bicycle': 'Bicycle',
    'book.pet': 'Pet',
    'book.luggage': 'Extra bags',
    'book.vehicle': 'Vehicle',
    'book.noVehicle': 'No vehicle',
    'book.seats': 'Seats',
    'book.seatsAssigned': 'Seats are assigned when you book — your party is kept together, and anyone needing priority is seated by a door so they are first off as well as first on.',
    'book.residentFares': 'Resident fares apply to this booking.',
    'book.visitorFares': 'Visitor fares. If you live in Puerto Rico, verify your residency in Account to pay the resident rate.',
    'book.continue': 'Continue — preview, no payment',
    'book.booking': 'Booking…',
    'book.confirmFirst': 'The fare is confirmed on the next screen before anything is charged.',
    'book.saved': 'Saved travellers',
    'book.savedHint': 'Tap to add someone you have travelled with before.',
    'book.saveThem': 'Remember these travellers for next time',
    'book.savedNote': 'Saved on your account so you do not type them again. Remove any of them in Account.',

    'pass.title': 'Your boarding pass',
    'pass.resident': 'RESIDENT',
    'pass.visitor': 'VISITOR',
    'pass.priority': 'PRIORITY',
    'pass.traveller': 'traveller',
    'pass.travellers': 'travellers',
    'pass.reference': 'Reference',
    'pass.sailing': 'Sailing',
    'pass.paid': 'Total',
    'pass.party': 'Your party',
    'pass.with': 'With you',
    'pass.oneScan': 'One scan boards everyone on this pass. Show this screen at the gate — you do not need to print anything.',
    'pass.seatAtGate': 'seat given at the gate',

    'status.held': 'Not paid yet',
    'status.heldWhy': 'Your seats are held. They are released if payment is not made before departure.',
    'status.paid': 'Paid',
    'status.paidWhy': 'Paid and ready. Show the code at the gate.',
    'status.boarded': 'Aboard',
    'status.boardedWhy': 'Scanned at the gate.',
    'status.cancelled': 'Cancelled',
    'status.cancelledWhy': 'This booking was cancelled.',
    'status.expired': 'Expired',
    'status.expiredWhy': 'The hold ran out and the seats went back on sale.',

    'res.title': 'Residency',
    'res.verified': 'Verified',
    'res.pending': 'Waiting on review',
    'res.rejected': 'Not verified',
    'res.vehicles': 'Vehicles',

    'sail.gone': 'Departed',
    'sail.closed': 'Closed',
    'sail.alreadyGone': 'earlier sailings today',
    'sail.lastTo': 'Last ferry to',
    'sail.bookingCloses': 'Booking closes',
    'sail.bookingClosed': 'Booking has closed for this one.',
    'sail.noneLeft': 'No more sailings today to',
    'sail.closesAt': 'Check-in closes',
    'sail.vehiclesBy': 'vehicles by',
    'sail.closedAt': 'Check-in closed at',
    'sail.alreadyLeft': 'This sailing has already left.',
    'sail.wasCancelled': 'This sailing was cancelled.',
    'sail.soldOut': 'This sailing is full.',

    'plan.leaveAt': 'Leave home at',
    'plan.shouldHaveLeft': 'You should have left at',
    'plan.tellUsHowFar': 'Tell us how far you are from the terminal and this plan will start at your front door.',
    'plan.minutesAway': 'Minutes from the terminal',
    'plan.gettingThere': 'Getting there',
    'plan.driving': 'Driving and parking',
    'plan.droppedOff': 'Being dropped off',
    'plan.taxi': 'Taxi',
    'plan.walking': 'Walking',
    'plan.deadline': 'Miss this and you miss the boat',
    'plan.doorToDoor': 'door to door',

    'pay.title': 'Reserve your trip (preview)',
    'pay.back': 'Back',
    'pay.amount': 'Amount',
    'pay.payCard': 'Confirm preview booking — no charge',
    'pay.payCounter': 'Preview: pay at the counter',
    'pay.working': 'Processing…',
    'pay.finish': 'Pay now',
    'pay.holdNote': 'Your seats are held until departure. Nothing is charged until you pay.',
    'pay.demoTitle': 'This is a demonstration',
    'pass.previewTitle': 'Preview pass — not valid for travel',
    'pass.previewBody': 'No payment was taken. Buy your ticket from the ferry operator until booking here goes live.',
    'pay.demoBody': 'No card is charged and no card details are asked for. Evertec is not connected yet — this shows the flow only.',

    'trips.noneTitle': 'No trips yet',
    'trips.noneBody': 'Book a crossing and it will show up here with a plan for the day.',
    'trips.noneUpcoming': 'Nothing coming up.',
    'trips.one': 'trip coming up',
    'trips.many': 'trips coming up',
    'trips.unpaid': 'not paid',
    'trips.past': 'past trips',
    'trips.timeToGo': 'Time to go',
    'trips.showPass': 'Show boarding pass',
    'trips.cancel': 'Cancel this trip',

    'sail.morning': 'Morning',
    'sail.afternoon': 'Afternoon',
    'sail.evening': 'Evening',
    'sail.changeRoute': 'Change route',
    'theme.light': 'Switch to light',
    'theme.dark': 'Switch to dark',
    'passport.title': 'Island passport',
    'passport.sub': 'A stamp for every crossing you make, stamped at the gate when your pass is scanned. Just for fun — stamps never change a fare.',
    'passport.crossings': 'crossings',
    'passport.travellers': 'travellers aboard',
    'passport.stamps': 'stamps',
    'passport.next': 'Next stamp',
    'passport.empty': 'Your first stamp lands when your pass is scanned at the gate.',
    'passport.earned': 'Stamped',
    'passport.locked': 'Not yet',
    'passport.open': 'Open passport',
    'passport.teaser': 'Sign in and every crossing you make earns a stamp — Vieques, Culebra, the sunrise boat and more.',
    'stamp.first': 'First crossing',
    'stamp.first.hint': 'Board any ferry.',
    'stamp.vieques': 'Vieques',
    'stamp.vieques.hint': 'Cross to or from Vieques.',
    'stamp.culebra': 'Culebra',
    'stamp.culebra.hint': 'Cross to or from Culebra.',
    'stamp.bothIslands': 'Both islands',
    'stamp.bothIslands.hint': 'Visit Vieques and Culebra.',
    'stamp.sunrise': 'Sunrise boat',
    'stamp.sunrise.hint': 'Board a sailing before 7 AM.',
    'stamp.sunset': 'Evening boat',
    'stamp.sunset.hint': 'Board a sailing at 6 PM or later.',
    'stamp.family': 'Whole crew',
    'stamp.family.hint': 'Board with a party of four or more.',
    'stamp.roundTrip': 'Out and back',
    'stamp.roundTrip.hint': 'Cross both ways on the same day.',
    'stamp.regular': 'Regular',
    'stamp.regular.hint': 'Make 10 crossings.',
    'stamp.islander': 'Islander',
    'stamp.islander.hint': 'Make 50 crossings.',

    'device.rename': 'Rename',
    'device.name': 'What is this device called?',
    'device.save': 'Save',
    'device.thisOne': 'the one you are using now',
    'ops.nothingToday': 'Nothing sailing today.',
    'ops.departs': 'Departs',
    'ops.vessel': 'Vessel',
    'ops.booked': 'Booked',
    'ops.aboard': 'Aboard',
    'ops.stillToBoard': 'Still to board',
    'ops.status': 'Status',

    'crew.title': 'Report position',
    'crew.intro': 'Only while a sailing is underway. Your phone sends its location about once a minute, which is as often as the map redraws.',
    'crew.pickSailing': 'Which sailing are you on?',
    'crew.noneUnderway': 'No sailing is underway right now — this only works once a boat has actually departed.',
    'crew.start': 'Start reporting',
    'crew.stop': 'Stop reporting',
    'crew.running': 'Reporting every minute.',
    'crew.stoppedArrived': 'That sailing is no longer underway, so reporting has stopped.',
    'crew.locationDenied': 'Location access was denied. Turn it on for this site in your phone settings, then start again.',
    'crew.locationUnsupported': 'This browser cannot get your location.',
    'crew.sent': 'sent',
    'crew.failed': 'failed to send',
    'crew.rejected': 'rejected — too fast to be real',
    'crew.lastAt': 'Last sent',

    'preview.title': 'Preview.',
    'preview.body': 'Not the official booking service. Sailing times are the real published schedule, but no payment is taken and no booking made here is real — do not travel on this.',
  },

  'es-PR': {
    'app.signIn': 'Entrar',
    'app.signOut': 'Salir',
    'app.loading': 'Cargando…',

    'auth.handle': 'Usuario',
    'auth.handlePlaceholder': 'tunombre',
    'auth.welcome': 'Bienvenido a bordo',
    'auth.welcomeSub': 'Escribe tu usuario. Si es tu primera vez, esto crea tu cuenta.',
    'auth.continue': 'Continuar con passkey',
    'auth.working': 'Un momento…',
    'auth.displayName': 'Tu nombre',
    'auth.createAccount': 'Crear cuenta',
    'auth.usePasskey': 'Entrar con passkey',
    'auth.noPasskeySupport': 'Este navegador no puede usar passkeys. Prueba Chrome, Safari o Edge.',
    'auth.setupToken': 'Código de configuración',
    'auth.haveSetupCode': '¿Tienes un código de configuración?',
    'auth.handleTaken': 'Ese nombre de usuario no está disponible.',
    'auth.setupTokenWhy': 'Solo hace falta al configurar una instalación nueva.',
    'auth.setupTokenWrong': 'Ese código no es correcto.',
    'auth.reassurePlatform': 'No hay contraseña que inventar ni que olvidar. Entras igual que desbloqueas tu teléfono — Face ID, huella, o tu gestor de contraseñas.',
    'auth.reassureKey': 'No hay contraseña que inventar ni que olvidar. Usa tu gestor de contraseñas, una llave de seguridad, o tu teléfono.',
    'auth.cancelled': 'Se canceló. No cambió nada.',

    'account.passkeys': 'Passkeys',
    'account.addPasskey': 'Añadir un passkey',
    'account.removePasskey': 'Quitar',
    'account.synced': 'Sincronizado con tu gestor de contraseñas',
    'account.deviceOnly': 'Solo en este equipo',
    'account.neverUsed': 'Nunca se ha usado',
    'account.lastUsed': 'Último uso',
    'account.recoveryWarning':
      'Esta cuenta tiene un solo passkey y no está sincronizado. Si pierdes ese equipo, no se puede recuperar la cuenta — añade un segundo passkey.',

    'notify.enable': 'Activar notificaciones',
    'notify.enabled': 'Las notificaciones están activadas',
    'notify.blocked': 'Las notificaciones están bloqueadas en tu navegador.',
    'notify.inbox': 'Notificaciones',
    'notify.empty': 'Nada todavía.',

    'admin.title': 'Administración',
    'admin.users': 'Usuarios',
    'admin.audit': 'Actividad',
    'admin.errors': 'Errores',
    'admin.flags': 'Flags',
    'admin.settings': 'Ajustes',
    'admin.broadcast': 'Mensaje',
    'admin.devices': 'Equipos',

    'env.beta': 'BETA — esta no es la app en vivo',
    'env.local': 'LOCAL',

    'error.generic': 'Algo salió mal.',
    'error.offline': 'Parece que no tienes conexión.',

    'update.ready': 'Hay una versión más nueva',
    'update.now': 'Actualizar',
    'update.later': 'Ahora no',

    'install.title': 'Ponlo en tu pantalla de inicio',
    'install.why': 'Tiene su propio ícono y abre sin la barra del navegador.',
    'install.ios': 'Toca Compartir y luego “Añadir a inicio”.',
    'install.add': 'Añadir',
    'install.no': 'Ahora no',

    'nav.book': 'Reservar',
    'nav.trips': 'Mis viajes',
    'nav.account': 'Cuenta',
    'nav.gate': 'Portón',
    'nav.ops': 'Operaciones',
    'nav.report': 'Posición',

    'sail.title': 'Próximas lanchas',
    'sail.back': 'Todas las lanchas',
    'sail.date': 'Fecha',
    'sail.route': 'Ruta',
    'sail.allRoutes': 'Todas las rutas',
    'sail.none': 'No hay salidas programadas para este día.',
    'sail.seatsLeft': 'asientos',
    'sail.full': 'Lleno',
    'sail.cancelled': 'Cancelada',
    'sail.cargoOnly': 'Solo carga',
    'sail.hazmat': 'Viaje con material peligroso. No lleva pasajeros.',
    'sail.cargoNotice': 'Viaje solo de carga. No lleva pasajeros.',
    'sail.unconfirmed': 'Sin confirmar',
    'sail.unconfirmedNotice': 'El itinerario todavía no indica si esta salida lleva pasajeros, así que no se vende aquí. Consulta en el terminal.',
    'sail.printedAs': 'El itinerario impreso dice',
    'sail.arrived': 'Llegó',
    'sail.underway': 'En camino',
    'sail.boarding': 'Abordando',
    'sail.late': 'min tarde',
    'sail.book': 'Reservar viaje (vista previa)',
    'sail.signInToBook': 'Entra para reservar.',
    'sail.carsAndPeople': 'Carros y pasajeros',
    'sail.peopleOnly': 'Solo pasajeros',
    'sail.cargo': 'Carga',
    'sail.departs': 'Sale',
    'sail.booked': 'reservados',

    'book.who': 'Quién viaja',
    'book.passenger': 'Pasajero',
    'book.name': 'Nombre',
    'book.nameHint': 'Igual que la ID',
    'book.fare': 'Tarifa',
    'book.adult': 'Adulto',
    'book.child': 'Niño',
    'book.senior': '55 o más',
    'book.priority': 'Necesita abordaje prioritario — 55 o más, lesionado, o movilidad limitada',
    'book.priorityNote': 'Algo que la tripulación deba saber (opcional)',
    'book.remove': 'Quitar',
    'book.addPassenger': 'Añadir otro pasajero',
    'book.tooMany': 'Los grupos de más de 12 se reservan en la ventanilla, para que la tripulación pueda planificar la cubierta.',
    'book.bringing': 'Qué llevas contigo',
    'book.bicycle': 'Bicicleta',
    'book.pet': 'Mascota',
    'book.luggage': 'Maletas extra',
    'book.vehicle': 'Vehículo',
    'book.noVehicle': 'Sin vehículo',
    'book.seats': 'Asientos',
    'book.seatsAssigned': 'Los asientos se asignan al reservar — tu grupo va junto, y quien necesite prioridad va cerca de la puerta para salir primero además de entrar primero.',
    'book.residentFares': 'Se aplican tarifas de residente.',
    'book.visitorFares': 'Tarifas de visitante. Si vives en Puerto Rico, verifica tu residencia en Cuenta para pagar la tarifa de residente.',
    'book.continue': 'Continuar — vista previa, sin pago',
    'book.booking': 'Reservando…',
    'book.confirmFirst': 'La tarifa se confirma en la próxima pantalla antes de cobrar nada.',
    'book.saved': 'Viajeros guardados',
    'book.savedHint': 'Toca para añadir a alguien con quien ya has viajado.',
    'book.saveThem': 'Recordar estos viajeros para la próxima',
    'book.savedNote': 'Se guardan en tu cuenta para no escribirlos otra vez. Puedes quitarlos en Cuenta.',

    'pass.title': 'Tu pase de abordaje',
    'pass.resident': 'RESIDENTE',
    'pass.visitor': 'VISITANTE',
    'pass.priority': 'PRIORIDAD',
    'pass.traveller': 'viajero',
    'pass.travellers': 'viajeros',
    'pass.reference': 'Referencia',
    'pass.sailing': 'Salida',
    'pass.paid': 'Total',
    'pass.party': 'Tu grupo',
    'pass.with': 'Contigo',
    'pass.oneScan': 'Un escaneo aborda a todos en este pase. Muestra esta pantalla en el portón — no hace falta imprimir nada.',
    'pass.seatAtGate': 'asiento asignado en el portón',

    'status.held': 'Sin pagar',
    'status.heldWhy': 'Tus asientos están apartados. Se liberan si no se paga antes de la salida.',
    'status.paid': 'Pagado',
    'status.paidWhy': 'Pagado y listo. Muestra el código en el portón.',
    'status.boarded': 'A bordo',
    'status.boardedWhy': 'Escaneado en el portón.',
    'status.cancelled': 'Cancelado',
    'status.cancelledWhy': 'Esta reserva fue cancelada.',
    'status.expired': 'Vencido',
    'status.expiredWhy': 'Se venció el apartado y los asientos volvieron a la venta.',

    'res.title': 'Residencia',
    'res.verified': 'Verificada',
    'res.pending': 'En revisión',
    'res.rejected': 'No verificada',
    'res.vehicles': 'Vehículos',

    'sail.gone': 'Salió',
    'sail.closed': 'Cerrado',
    'sail.alreadyGone': 'salidas anteriores de hoy',
    'sail.lastTo': 'Última lancha a',
    'sail.bookingCloses': 'Las reservas cierran',
    'sail.bookingClosed': 'Ya cerraron las reservas para esta.',
    'sail.noneLeft': 'No quedan salidas hoy a',
    'sail.closesAt': 'El registro cierra',
    'sail.vehiclesBy': 'vehículos antes de',
    'sail.closedAt': 'El registro cerró a las',
    'sail.alreadyLeft': 'Esta salida ya se fue.',
    'sail.wasCancelled': 'Esta salida fue cancelada.',
    'sail.soldOut': 'Esta salida está llena.',

    'plan.leaveAt': 'Sal de casa a las',
    'plan.shouldHaveLeft': 'Debiste salir a las',
    'plan.tellUsHowFar': 'Dinos a qué distancia estás del terminal y el plan empezará en tu puerta.',
    'plan.minutesAway': 'Minutos del terminal',
    'plan.gettingThere': 'Cómo llegas',
    'plan.driving': 'Manejando y estacionando',
    'plan.droppedOff': 'Me dejan',
    'plan.taxi': 'Taxi',
    'plan.walking': 'Caminando',
    'plan.deadline': 'Si lo pierdes, pierdes la lancha',
    'plan.doorToDoor': 'de puerta a puerta',

    'pay.title': 'Reserva tu viaje (vista previa)',
    'pay.back': 'Atrás',
    'pay.amount': 'Total',
    'pay.payCard': 'Confirmar reserva de prueba — sin cobro',
    'pay.payCounter': 'Vista previa: pagar en la ventanilla',
    'pay.working': 'Procesando…',
    'pay.finish': 'Pagar ahora',
    'pay.holdNote': 'Tus asientos están apartados hasta la salida. No se cobra nada hasta que pagues.',
    'pay.demoTitle': 'Esto es una demostración',
    'pass.previewTitle': 'Pase de prueba — no es válido para viajar',
    'pass.previewBody': 'No se cobró nada. Compra tu boleto con el operador de la lancha hasta que la reserva aquí esté activa.',
    'pay.demoBody': 'No se cobra ninguna tarjeta y no se piden datos de tarjeta. Evertec todavía no está conectado — esto solo muestra el proceso.',

    'trips.noneTitle': 'Todavía no hay viajes',
    'trips.noneBody': 'Reserva una salida y aparecerá aquí con un plan para el día.',
    'trips.noneUpcoming': 'Nada próximo.',
    'trips.one': 'viaje próximo',
    'trips.many': 'viajes próximos',
    'trips.unpaid': 'sin pagar',
    'trips.past': 'viajes pasados',
    'trips.timeToGo': 'Hora de salir',
    'trips.showPass': 'Ver pase de abordaje',
    'trips.cancel': 'Cancelar este viaje',

    'sail.morning': 'Mañana',
    'sail.afternoon': 'Tarde',
    'sail.evening': 'Noche',
    'sail.changeRoute': 'Cambiar ruta',
    'theme.light': 'Cambiar a claro',
    'theme.dark': 'Cambiar a oscuro',
    'passport.title': 'Pasaporte isleño',
    'passport.sub': 'Un sello por cada cruce, que se estampa en el portón cuando escanean tu pase. Es solo por diversión — los sellos nunca cambian la tarifa.',
    'passport.crossings': 'cruces',
    'passport.travellers': 'pasajeros a bordo',
    'passport.stamps': 'sellos',
    'passport.next': 'Próximo sello',
    'passport.empty': 'Tu primer sello llega cuando escanean tu pase en el portón.',
    'passport.earned': 'Sellado',
    'passport.locked': 'Todavía no',
    'passport.open': 'Ver pasaporte',
    'passport.teaser': 'Entra y cada cruce que hagas gana un sello — Vieques, Culebra, la lancha del amanecer y más.',
    'stamp.first': 'Primer cruce',
    'stamp.first.hint': 'Aborda cualquier lancha.',
    'stamp.vieques': 'Vieques',
    'stamp.vieques.hint': 'Cruza hacia o desde Vieques.',
    'stamp.culebra': 'Culebra',
    'stamp.culebra.hint': 'Cruza hacia o desde Culebra.',
    'stamp.bothIslands': 'Las dos islas',
    'stamp.bothIslands.hint': 'Visita Vieques y Culebra.',
    'stamp.sunrise': 'Lancha del amanecer',
    'stamp.sunrise.hint': 'Aborda una salida antes de las 7 AM.',
    'stamp.sunset': 'Lancha de la tarde',
    'stamp.sunset.hint': 'Aborda una salida de 6 PM en adelante.',
    'stamp.family': 'Toda la familia',
    'stamp.family.hint': 'Aborda con un grupo de cuatro o más.',
    'stamp.roundTrip': 'Ida y vuelta',
    'stamp.roundTrip.hint': 'Cruza ida y vuelta el mismo día.',
    'stamp.regular': 'De siempre',
    'stamp.regular.hint': 'Haz 10 cruces.',
    'stamp.islander': 'Isleño',
    'stamp.islander.hint': 'Haz 50 cruces.',

    'device.rename': 'Renombrar',
    'device.name': '¿Cómo se llama este equipo?',
    'device.save': 'Guardar',
    'device.thisOne': 'el que estás usando ahora',
    'ops.nothingToday': 'No hay salidas hoy.',
    'ops.departs': 'Sale',
    'ops.vessel': 'Embarcación',
    'ops.booked': 'Reservados',
    'ops.aboard': 'A bordo',
    'ops.stillToBoard': 'Faltan por abordar',
    'ops.status': 'Estado',

    'crew.title': 'Reportar posición',
    'crew.intro': 'Solo mientras una salida está en camino. Tu teléfono envía su ubicación cerca de una vez por minuto, que es tan seguido como se actualiza el mapa.',
    'crew.pickSailing': '¿En cuál salida estás?',
    'crew.noneUnderway': 'Ninguna salida está en camino ahora mismo — esto solo funciona una vez que un barco ya salió.',
    'crew.start': 'Empezar a reportar',
    'crew.stop': 'Dejar de reportar',
    'crew.running': 'Reportando cada minuto.',
    'crew.stoppedArrived': 'Esa salida ya no está en camino, así que se detuvo el reporte.',
    'crew.locationDenied': 'Se denegó el acceso a la ubicación. Actívala para este sitio en los ajustes del teléfono y vuelve a empezar.',
    'crew.locationUnsupported': 'Este navegador no puede obtener tu ubicación.',
    'crew.sent': 'enviado',
    'crew.failed': 'no se pudo enviar',
    'crew.rejected': 'rechazado — demasiado rápido para ser real',
    'crew.lastAt': 'Último envío',

    'preview.title': 'Vista previa.',
    'preview.body': 'Este no es el servicio oficial de reservas. Los horarios son los oficiales publicados, pero no se cobra nada y ninguna reserva aquí es real — no viajes con esto.',
  },
} as const

export type StringKey = keyof (typeof STRINGS)['en']

let locale: Locale = detect()

function detect(): Locale {
  const stored = localStorage.getItem('locale')
  if (stored === 'en' || stored === 'es-PR') return stored
  return navigator.language.toLowerCase().startsWith('es') ? 'es-PR' : 'en'
}

export function getLocale(): Locale {
  return locale
}

export function setLocale(next: Locale): void {
  locale = next
  localStorage.setItem('locale', next)
  document.documentElement.lang = next
}

/**
 * A missing translation falls back to English rather than rendering the key.
 * A user seeing `account.passkeys` learns nothing; a user seeing the English
 * learns what the control does.
 */
export function t(key: StringKey): string {
  return STRINGS[locale][key] ?? STRINGS.en[key] ?? key
}
